import { useState, useEffect, useCallback } from "react";
import {
  Mic,
  FileText,
  AlertCircle,
  Sparkles,
  Calendar,
  UserCheck,
  Clock,
  Users,
  Code,
  ShieldCheck,
  TrendingDown,
  CreditCard,
  Building2,
  Pill,
  ClipboardCheck,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const HERO_BLUE = "#0d3b6e";

type BulletItem = {
  text: string;
  icon: LucideIcon;
};

type Feature = {
  id: string;
  title: string;
  description: string;
  image: string;
  bullets: BulletItem[];
};

const features: Feature[] = [
  {
    id: "clinical",
    title: "Clinical",
    description: "AI-powered tools that reduce documentation burden and enhance clinical decision-making.",
    image: "/assets/hero/healthcare_administr_a7e7b5e0.jpg",
    bullets: [
      { text: "Ambient listening captures patient encounters automatically", icon: Mic },
      { text: "Intelligent chart preparation before each visit", icon: FileText },
      { text: "Real-time clinical decision support alerts", icon: AlertCircle },
      { text: "One-click note generation with AI assistance", icon: Sparkles },
    ],
  },
  {
    id: "practice",
    title: "Practice Management",
    description: "Streamline operations with AI-informed scheduling, triage, and patient flow.",
    image: "/assets/hero/healthcare_practice_management_office.png",
    bullets: [
      { text: "Smart scheduling optimizes provider utilization", icon: Calendar },
      { text: "Automated patient check-in and triage", icon: UserCheck },
      { text: "Real-time wait time predictions", icon: Clock },
      { text: "Staff workflow automation and task routing", icon: Users },
    ],
  },
  {
    id: "revenue",
    title: "Revenue Cycle",
    description: "Maximize reimbursement with AI-supported coding and claims management.",
    image: "/assets/hero/medical_billing_revenue_cycle.png",
    bullets: [
      { text: "AI-assisted coding suggestions for accuracy", icon: Code },
      { text: "Pre-claim validation catches errors early", icon: ShieldCheck },
      { text: "Denial pattern analysis and prevention", icon: TrendingDown },
      { text: "Real-time eligibility verification", icon: CreditCard },
    ],
  },
  {
    id: "occmed",
    title: "Occupational Medicine",
    description: "Purpose-built workflows for employer services and compliance.",
    image: "/assets/hero/occupational_medicine_workplace_health.png",
    bullets: [
      { text: "Employer portal for scheduling and reporting", icon: Building2 },
      { text: "Drug screening and physical exam workflows", icon: Pill },
      { text: "OSHA compliance tracking and reporting", icon: ClipboardCheck },
      { text: "Return-to-work clearance automation", icon: RefreshCw },
    ],
  },
];

export default function FeaturesShowcase() {
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextTab = useCallback(() => {
    setActiveTab((prev) => (prev + 1) % features.length);
  }, []);

  // Auto-rotate tabs
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(nextTab, 5000);
    return () => clearInterval(interval);
  }, [isPaused, nextTab]);

  const activeFeature = features[activeTab];

  return (
    <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
      {/* Feature content */}
      <div
        className="rounded-2xl backdrop-blur-md border border-white/30 shadow-xl overflow-hidden transition-all duration-300 p-6 lg:p-8"
        style={{
          background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${HERO_BLUE} 100%)`
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Section header - white text, centered */}
        <div className="mb-8 animate-fade-in-up">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4 text-white">
              Platform Capabilities
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-white">
              Explore how our AI-native platform transforms every aspect of urgent care operations.
            </p>
          </div>
        </div>

        {/* Tabs - dark blue with 3D effect, centered */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {features.map((feature, idx) => (
            <button
              key={feature.id}
              onClick={() => {
                setActiveTab(idx);
                setIsPaused(true);
              }}
              className={`px-6 py-3 rounded-full font-bold text-sm sm:text-base transition-all duration-200 hover:-translate-y-1 active:translate-y-0 ${
                activeTab === idx ? "scale-105" : ""
              }`}
              style={{
                backgroundColor: activeTab === idx ? PRIMARY_BLUE : HERO_BLUE,
                color: "white",
                boxShadow: activeTab === idx
                  ? "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)"
                  : "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {feature.title}
            </button>
          ))}
        </div>

        {/* Carousel content */}
        <div className="grid lg:grid-cols-2 gap-0 rounded-xl overflow-hidden">
          {/* Left: Image */}
          <div className="relative h-96 lg:h-auto lg:min-h-[600px] overflow-hidden">
            <img
              src={activeFeature.image}
              alt={activeFeature.title}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-500"
            />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${PRIMARY_BLUE}80 0%, ${HERO_BLUE}60 100%)`,
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white p-8">
                <h3 className="text-3xl sm:text-4xl font-bold mb-4">{activeFeature.title}</h3>
                <p className="text-lg text-white/90 max-w-md">{activeFeature.description}</p>
              </div>
            </div>
          </div>

          {/* Right: Bullets */}
          <div className="p-8 lg:p-12 flex flex-col justify-center bg-white/5">
            <h4 className="text-2xl lg:text-3xl font-normal mb-8 text-white">
              Key Capabilities
            </h4>
            <ul className="space-y-6">
              {activeFeature.bullets.map((bullet, idx) => {
                const Icon = bullet.icon;
                return (
                  <li
                    key={idx}
                    className="flex items-start gap-4 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                    >
                      <Icon className="w-6 h-6" style={{ color: "#0AB89E" }} />
                    </div>
                    <span className="text-lg lg:text-xl text-white/90 leading-relaxed pt-2">
                      {bullet.text}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Progress indicator */}
            <div className="mt-10 flex gap-2">
              {features.map((_, idx) => (
                <div
                  key={idx}
                  className="h-1.5 rounded-full flex-1 overflow-hidden bg-white/20"
                >
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      idx === activeTab ? "animate-progress" : idx < activeTab ? "w-full" : "w-0"
                    }`}
                    style={{
                      backgroundColor: "white",
                      animationDuration: isPaused ? "0s" : "5s",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
